// The in-memory API's responsibilities, recurring work and templates (M1-05, M1-06). The rules
// are the API's (services/api/src/routes/org/responsibilities.ts, domain/templates/apply.ts),
// and the pure parts are the API's own code from `@companyos/domain-work`:
//   - schedules: `parseRule` / `formatRule` (the stored text is the normalised one; else `422
//     schedule_invalid`) and `nextAfter`, anchored at `created_at`, in the company's time zone;
//   - responsibilities are read by the seat's viewers (a holder of the seat, a holder of a seat
//     above it, or a manager) and written by managers; a closed seat refuses new ones (`409
//     seat_closed`); archiving pauses the active recurring work (`next_run_at` null); restoring
//     resumes nothing; deleting removes its recurring work; weights are numeric(6,3) text;
//   - recurring work: `409 conflict` under an archived responsibility, `409 seat_closed` on a
//     closed seat, resume refused (409) while either holds; `materialise-now` records the
//     occurrence at `next_run_at` (no task until boards exist), moves `next_run_at` to the first
//     occurrence after max(that occurrence, now), and is `409 conflict` while paused;
//   - templates: `checkSpec` at create/update, `resolveSpec` at preview/apply, and the plan of
//     `planTemplate` line for line (reasons included). Spaces, channels, boards and starter
//     tasks go through the work twin's template handlers (fake-work.ts, as the API's registered
//     chat and boards handlers), and are skipped with `NOT_BUILT` only when none are given.
//     Every company starts with `CLIENT_ONBOARDING` (`SEEDED_TEMPLATES`).
import { formatRule, nextAfter, parseRule, type Rule } from "@companyos/domain-work/recurrence";
import { checkSpec, resolveSpec, SEEDED_TEMPLATES } from "@companyos/domain-work/templates";
import type { Problem, RouteId } from "@companyos/sdk";
import type { Handler, Principal } from "./fake";
import { type FakeCompany, uid } from "./fake-data";
import type { KpiStanding } from "./fake-kpi-visibility";
import type { FakeWorkTemplateHandlers } from "./fake-work";
import type {
  Goal,
  GoalMilestone,
  MetricDef,
  RecurringWork,
  Responsibility,
  Role,
  Seat,
  SeatAssignment,
  TaskTemplate,
  Template,
  TemplateApplied,
  TemplatePlanItem,
  TemplatePreview,
  TemplateSpec,
} from "./types";

export type OrgDeps = {
  json: (body: unknown, status?: number) => Response;
  problem: (status: number, code: Problem["code"], title: string, detail?: string) => Response;
  notFound: () => Response;
  validation: (detail: string) => Response;
  page: <T>(items: T[], url: URL) => unknown;
  now: () => Date;
  nextSeq: () => number;
  /** The structure fake's seat list of a company (mutable: `apply` adds to it). */
  seatsOf: (company: FakeCompany) => Seat[];
  /** The structure fake's metric definitions of a company. */
  metricsOf: (company: FakeCompany) => MetricDef[];
  /** The structure fake's seat assignments (who holds which seat). */
  assignmentsOf: (company: FakeCompany) => SeatAssignment[];
  /** The access fake's roles of a company (mutable: `apply` adds to it). */
  rolesOf: (companyId: string) => Role[];
  /** KPI-VISIBILITY: the caller's KPI standing (fake-kpi-visibility.ts), for the Keystones audience. */
  kpiStanding?: (p: Principal) => KpiStanding;
  /**
   * The work twin's side of `templates.apply` (fake-work.ts), as services/api registers its chat
   * and boards handlers; null: spaces, channels, boards and tasks are skipped (`NOT_BUILT`).
   */
  templateHandlers?: () => FakeWorkTemplateHandlers | null;
  /**
   * CHART-DIMENSIONS: where this twin hands the chart its responsibility store (the floor);
   * CHART-KB: and its goals (the quarter's priorities are the Keystones lens).
   */
  share?: (stores: {
    responsibilitiesOf: (c: FakeCompany) => Responsibility[];
    goalsOf: (c: FakeCompany) => Goal[];
    /** Parity P5: the recurring work rows the routine controls act on (fake-process.ts). */
    recurringOf?: (c: FakeCompany) => RecurringWork[];
  }) => void;
  /** FOUNDER-OS: a company's own seeded priorities (the portfolio's, fake-portfolio.ts). */
  seedGoalsOf?: (c: FakeCompany, quarter: string) => Goal[] | null;
};

/** CHART-KB: the calendar quarter of a date (`2026-Q4`); the twin's companies run calendar years. */
export const quarterOf = (d: Date) =>
  `${d.getUTCFullYear()}-Q${Math.floor(d.getUTCMonth() / 3) + 1}`;

/** PRIORITIES-HOME: the quarter after a quarter key. */
export const nextQuarterOf = (key: string) => {
  const year = Number(key.slice(0, 4));
  const q = Number(key.slice(6));
  return q === 4 ? `${year + 1}-Q1` : `${year}-Q${q + 1}`;
};

type FakeTeam = { company_id: string; id: string; name: string };

const SEED_AT = "2026-06-01T09:00:00Z";
const companyNo = (companyId: string) => Number.parseInt(companyId.slice(-4), 16) % 0x0fff;

/**
 * The API's reasons for skipping a template's work entries while no handler is registered
 * (services/api `work-hooks.ts NOT_BUILT`).
 */
export const NOT_BUILT = {
  spaces: "Chat (spaces and channels) is not built yet; the space is not created",
  boards: "Boards are not built yet; the board and its tasks are not created",
} as const;

/** The API's reasons when an agency operator applies (services/api `NO_MEMBERSHIP`). */
export const NO_MEMBERSHIP = {
  spaces: "Spaces are created by a member of the company; an operator's application skips them",
  boards: "Boards are created in a space by a member; an operator's application skips them",
} as const;

type Item = TemplatePlanItem;
const item = (
  kind: Item["kind"],
  name: string,
  action: Item["action"],
  o: {
    key?: string | null;
    existing?: string | null;
    parent?: string | null;
    reason?: string | null;
  } = {},
): Item => ({
  kind,
  key: o.key ?? null,
  name,
  action,
  existing_id: o.existing ?? null,
  parent: o.parent ?? null,
  reason: o.reason ?? null,
});

/** numeric(6,3) text (`1.500`), or null when out of range (≥ 0, < 1000, ≤ 3 fraction digits). */
export function weightOf(v: unknown): string | null {
  const n =
    typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : Number.NaN;
  if (!Number.isFinite(n) || n < 0 || n >= 1000) return null;
  if (Math.round(n * 1000) / 1000 !== n) return null;
  return n.toFixed(3);
}

export function orgHandlers(d: OrgDeps): Partial<Record<RouteId, Handler>> {
  const { json, problem, notFound, validation } = d;
  const forbidden = () => problem(403, "forbidden", "Forbidden");
  const stamp = () => d.now().toISOString();
  const actor = (p: Principal) => ({
    actor_kind: p.operator ? ("agency_user" as const) : ("person" as const),
    actor_membership_id: null,
  });
  const noContent = () => new Response(null, { status: 204 });
  const tzOf = (c: FakeCompany) => c.calendar_config.timezone;

  const responsibilityStore = new Map<string, Responsibility[]>();
  const recurringStore = new Map<string, RecurringWork[]>();
  const templateStore = new Map<string, Template[]>();
  const teamStore = new Map<string, FakeTeam[]>();

  const listOf = <T>(store: Map<string, T[]>, id: string): T[] => {
    let list = store.get(id);
    if (!list) {
      list = [];
      store.set(id, list);
    }
    return list;
  };
  /**
   * CHART-DIMENSIONS: the demo's responsibilities, so the chart's cards carry accountabilities.
   * Seeded once per company: three on every person-held seat and two on Acme's Sourcer (DE) seat.
   * Riley's seat and the open seat stay empty (the seat drawer's "No responsibilities yet." and
   * the template preview count on them).
   */
  const SEEDED_RESPONSIBILITIES: Record<"person" | "digital_employee", [string, string][]> = {
    person: [
      ["Pipeline hygiene", "2.000"],
      ["Weekly forecast", "1.500"],
      ["Client reviews", "1.000"],
    ],
    digital_employee: [
      ["Candidate sourcing", "2.000"],
      ["Outreach replies", "1.000"],
    ],
  };
  const SOURCER_SEAT = uid(0x2000, 0xa02);
  function seedResponsibilities(c: FakeCompany): Responsibility[] {
    const out: Responsibility[] = [];
    const held = new Map(
      d
        .assignmentsOf(c)
        .filter((a) => a.active_to === null)
        .map((a) => [a.seat_id, a.membership_kind]),
    );
    for (const s of d.seatsOf(c)) {
      const kind = held.get(s.id);
      if (!kind || kind === "system_agent") continue;
      if (kind === "digital_employee" && s.id !== SOURCER_SEAT) continue;
      SEEDED_RESPONSIBILITIES[kind].forEach(([name, weight], i) => {
        out.push({
          company_id: c.id,
          id: uid(0x2e00 + out.length, i + 1),
          seat_id: s.id,
          name,
          description: null,
          weight,
          kpi_metric_ids: [],
          status: "active",
          actor_kind: "system",
          actor_membership_id: null,
          created_at: "2026-06-01T09:00:00Z",
          updated_at: "2026-06-01T09:00:00Z",
        });
      });
    }
    return out;
  }
  const responsibilitiesOf = (c: FakeCompany) => {
    let list = responsibilityStore.get(c.id);
    if (!list) {
      list = seedResponsibilities(c);
      responsibilityStore.set(c.id, list);
    }
    return list;
  };
  const recurringOf = (c: FakeCompany) => listOf(recurringStore, c.id);
  const teamsOf = (c: FakeCompany) => listOf(teamStore, c.id);

  // CHART-KB: goals (`kind = priority` is a quarter's keystone on the org chart) with their
  // milestones, as services/api/src/routes/org/goals.ts: read by every member of the company,
  // written by a manager or the seat's holder, the quarter defaulting to the current one.
  // Seeded once per company so the Keystones lens and the completeness have something to show:
  // two priorities on every person-held seat (one on track with a milestone done, one at risk),
  // one on Acme's Sourcer (DE) seat; Riley's seat and the open seat carry none (a dimmed card,
  // a missing keystone).
  const goalStore = new Map<string, Goal[]>();
  const SEEDED_PRIORITIES: [
    title: string,
    track: Goal["track"],
    milestones: [string, boolean][],
  ][] = [
    [
      "Fill the pipeline for Q4 searches",
      "on",
      [
        ["Brief every open search", true],
        ["Shortlist ready on each", false],
      ],
    ],
    [
      "Cut time-to-offer to 21 days",
      "at_risk",
      [
        ["Interview loop agreed with clients", false],
        ["Offer template signed off", false],
      ],
    ],
  ];
  function seedGoals(c: FakeCompany): Goal[] {
    const out: Goal[] = [];
    const quarter = quarterOf(d.now());
    const own = d.seedGoalsOf?.(c, quarter);
    if (own) return own;
    const held = new Map(
      d
        .assignmentsOf(c)
        .filter((a) => a.active_to === null)
        .map((a) => [a.seat_id, a.membership_kind]),
    );
    for (const s of d.seatsOf(c)) {
      const kind = held.get(s.id);
      if (!kind) continue;
      const specs =
        kind === "digital_employee"
          ? s.id === SOURCER_SEAT
            ? SEEDED_PRIORITIES.slice(0, 1)
            : []
          : SEEDED_PRIORITIES;
      specs.forEach(([title, track, milestones], i) => {
        const id = uid(0x2f00 + out.length, i + 1);
        out.push({
          company_id: c.id,
          id,
          kind: "priority",
          seat_id: s.id,
          team_id: null,
          title,
          due_at: null,
          period_key: quarter,
          metric_def_id: null,
          target: null,
          state: "open",
          track,
          flagged_to_portfolio: false,
          portfolio_link_ref: null,
          vision_page_id: null,
          actor_kind: "system",
          actor_membership_id: null,
          created_at: SEED_AT,
          updated_at: SEED_AT,
          milestones: milestones.map(([mt, done], position) => ({
            company_id: c.id,
            id: uid(0x2f80 + out.length, position + 1),
            goal_id: id,
            title: mt,
            // CAL-WORK-LAYERS: milestones carry dates around today (the Milestones layer): a
            // done one last week, open ones in the next weeks; seats sharing a spec share the day.
            due_at: new Date(
              d.now().getTime() + (done ? -5 : i * 4 + position * 2 - 1) * 86_400_000,
            ).toISOString(),
            done_at: done ? SEED_AT : null,
            position,
            actor_kind: "system",
            actor_membership_id: null,
            created_at: SEED_AT,
            updated_at: SEED_AT,
          })),
        });
      });
    }
    return out;
  }
  const goalsOf = (c: FakeCompany) => {
    let list = goalStore.get(c.id);
    if (!list) {
      list = seedGoals(c);
      goalStore.set(c.id, list);
    }
    return list;
  };
  d.share?.({ responsibilitiesOf, goalsOf, recurringOf });

  function templatesOf(c: FakeCompany): Template[] {
    let list = templateStore.get(c.id);
    if (!list) {
      list = SEEDED_TEMPLATES.map((t, i) => ({
        company_id: c.id,
        id: uid(0x7e00 + companyNo(c.id), i + 1),
        name: t.name,
        description: t.description,
        spec: structuredClone(t.spec),
        version: 1,
        actor_kind: "system" as const,
        actor_membership_id: null,
        created_at: SEED_AT,
        updated_at: SEED_AT,
      }));
      templateStore.set(c.id, list);
    }
    return list;
  }

  const seatOf = (p: Principal, id: string | undefined) =>
    d.seatsOf(p.company).find((s) => s.id === id) ?? null;
  const responsibilityOf = (p: Principal, id: string | undefined) =>
    responsibilitiesOf(p.company).find((r) => r.id === id) ?? null;
  const recurringRow = (p: Principal, id: string | undefined) =>
    recurringOf(p.company).find((r) => r.id === id) ?? null;

  /** 05 `seat#viewer`: a manager, a holder of the seat, or a holder of a seat above it. */
  function canViewSeat(p: Principal, seat: Seat): boolean {
    if (p.manager) return true;
    const mine = new Set(
      p.me.memberships.filter((m) => m.company_id === p.company.id).map((m) => m.membership_id),
    );
    const held = new Set(
      d
        .assignmentsOf(p.company)
        .filter((a) => a.active_to === null && mine.has(a.membership_id))
        .map((a) => a.seat_id),
    );
    const seen = new Set<string>();
    for (
      let cur: Seat | null = seat;
      cur && !seen.has(cur.id);
      cur = seatOf(p, cur.supervisor_seat_id ?? undefined)
    ) {
      if (held.has(cur.id)) return true;
      seen.add(cur.id);
    }
    return false;
  }

  const ruleOf = (text: string): Rule | null => {
    const parsed = parseRule(text);
    return parsed.ok ? parsed.rule : null;
  };
  const nextOf = (p: Principal, w: Pick<RecurringWork, "schedule" | "created_at">, after: Date) => {
    const rule = ruleOf(w.schedule);
    return rule
      ? (nextAfter(rule, new Date(w.created_at), tzOf(p.company), after)?.toISOString() ?? null)
      : null;
  };

  /** A task template from a request body, or the reason it is refused. */
  function taskTemplate(v: unknown): TaskTemplate | string {
    if (!v || typeof v !== "object") return "task_template is required";
    const t = v as Partial<TaskTemplate>;
    if (typeof t.title !== "string" || !t.title.trim()) return "task_template.title is required";
    if (t.due_in_days !== undefined && (!Number.isInteger(t.due_in_days) || t.due_in_days < 0))
      return "task_template.due_in_days must be a whole number of days";
    if (
      t.priority !== undefined &&
      (!Number.isInteger(t.priority) || t.priority < 0 || t.priority > 4)
    )
      return "task_template.priority is 0 to 4";
    return {
      title: t.title.trim(),
      ...(t.description ? { description: t.description } : {}),
      ...(t.priority !== undefined ? { priority: t.priority } : {}),
      ...(t.assignee_seat_ids?.length ? { assignee_seat_ids: t.assignee_seat_ids } : {}),
      ...(t.checklist?.length ? { checklist: t.checklist } : {}),
      ...(t.due_in_days !== undefined ? { due_in_days: t.due_in_days } : {}),
    };
  }

  /** The normalised schedule text, or the 422 that refuses it. */
  function scheduleText(v: unknown): string | Response {
    if (typeof v !== "string") return validation("schedule is required");
    const parsed = parseRule(v);
    if (!parsed.ok)
      return problem(422, "schedule_invalid", "Not a valid RRULE schedule", parsed.reason);
    return formatRule(parsed.rule);
  }

  // Templates: the API's plan (planTemplate) -----------------------------------------------------

  type Plan = {
    problems: { path: string; message: string }[];
    params: Record<string, string>;
    items: Item[];
    spec: TemplateSpec;
    reuse: { roles: Map<string, string>; teams: Map<string, string> };
    metrics: Map<string, string>;
    handlers: FakeWorkTemplateHandlers | null;
  };

  function plan(p: Principal, template: Template, given: Record<string, string>): Plan {
    // An agency operator holds no membership, which chat writes need: the API skips the work
    // entries of its applications (`NO_MEMBERSHIP`).
    const handlers = p.operator ? null : (d.templateHandlers?.() ?? null);
    const built = handlers !== null;
    const reasons = p.operator ? NO_MEMBERSHIP : NOT_BUILT;
    const resolved = resolveSpec(template.spec, given);
    const spec = resolved.spec;
    const roles = d.rolesOf(p.company.id);
    const teams = teamsOf(p.company);
    const metrics = new Map(d.metricsOf(p.company).map((m) => [m.key, m.id]));
    const reuse = { roles: new Map<string, string>(), teams: new Map<string, string>() };
    const items: Item[] = [];
    for (const r of spec.roles ?? []) {
      const hit = roles.find((x) => x.name.toLowerCase() === r.name.toLowerCase());
      if (hit) reuse.roles.set(r.key, hit.id);
      items.push(
        item("role", r.name, hit ? "reuse" : "create", {
          key: r.key,
          existing: hit?.id ?? null,
          ...(hit ? { reason: "a role of this name exists; its seats use it" } : {}),
        }),
      );
    }
    const seatTitle = new Map((spec.seats ?? []).map((s) => [s.key, s.title]));
    const teamName = new Map((spec.teams ?? []).map((t) => [t.key, t.name]));
    for (const t of spec.teams ?? []) {
      const hit = teams.find((x) => x.name === t.name);
      if (hit) reuse.teams.set(t.key, hit.id);
      items.push(
        item("team", t.name, hit ? "reuse" : "create", {
          key: t.key,
          existing: hit?.id ?? null,
          parent: t.parent ? (teamName.get(t.parent) ?? null) : null,
          ...(hit && t.lead_seat ? { reason: "the existing team keeps its lead seat" } : {}),
        }),
      );
    }
    for (const s of spec.seats ?? []) {
      items.push(
        item("seat", s.title, "create", {
          key: s.key,
          parent: s.supervisor ? (seatTitle.get(s.supervisor) ?? null) : null,
          ...(s.supervisor ? { reason: `reports to ${seatTitle.get(s.supervisor)}` } : {}),
        }),
      );
      if (s.team)
        items.push(
          item("team_member", s.title, "create", { parent: teamName.get(s.team) ?? null }),
        );
    }
    for (const r of spec.responsibilities ?? []) {
      items.push(
        item("responsibility", r.name, "create", { parent: seatTitle.get(r.seat) ?? null }),
      );
      for (const key of r.metric_keys ?? []) {
        const id = metrics.get(key);
        items.push(
          item("metric", key, id ? "link" : "skip", {
            existing: id ?? null,
            parent: r.name,
            ...(id ? {} : { reason: "no metric with this key in the company" }),
          }),
        );
      }
      for (const x of r.recurring ?? [])
        items.push(
          item("recurring_work", x.task.title, "create", {
            parent: r.name,
            reason:
              x.board && !built
                ? `${x.schedule}; ${
                    p.operator
                      ? "recorded without a task (its board is not created)"
                      : "recorded without a task until boards exist"
                  }`
                : x.schedule,
          }),
        );
    }
    // Spaces, channels, boards and starter tasks: created through the work twin's handlers (as
    // the API's registered chat and boards handlers), skipped with the reason without them.
    const action = built ? "create" : "skip";
    for (const s of spec.spaces ?? []) {
      const spaces = built ? {} : { reason: reasons.spaces };
      const boards = built ? {} : { reason: reasons.boards };
      items.push(item("space", s.name, action, { key: s.key, ...spaces }));
      for (const c of s.channels ?? [])
        items.push(item("channel", c.name, action, { parent: s.name, ...spaces }));
      for (const b of s.boards ?? []) {
        items.push(item("board", b.name, action, { key: b.key, parent: s.name, ...boards }));
        // W1-FIXES: a board's custom fields and views are lines of the plan (as the API's)
        for (const f of b.fields ?? [])
          items.push(
            item("field", f.name, action, { parent: b.name, reason: boards.reason ?? f.type }),
          );
        for (const v of b.views ?? [])
          items.push(
            item("view", v.name, action, {
              parent: b.name,
              reason: boards.reason ?? `${v.kind}${v.is_default ? ", default" : ""}`,
            }),
          );
        for (const t of b.tasks ?? [])
          items.push(item("task", t.title, action, { parent: b.name, ...boards }));
      }
    }
    return {
      problems: resolved.problems,
      params: resolved.params,
      items,
      spec,
      reuse,
      metrics,
      handlers,
    };
  }

  function templateInvalid(problems: Plan["problems"]): Response {
    return new Response(
      JSON.stringify({
        type: "https://companyos.app/errors/template_invalid",
        title: "Template spec or params do not apply",
        status: 422,
        code: "template_invalid",
        request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
        ...(problems[0] ? { detail: `${problems[0].path}: ${problems[0].message}` } : {}),
        errors: problems.map((x) => ({ ...x, code: "template_invalid" })),
      }),
      { status: 422, headers: { "content-type": "application/problem+json" } },
    );
  }

  /** The spec of a create/update body, or the response that refuses it (`checkSpec`). */
  function specOf(v: unknown): TemplateSpec | Response {
    if (!v || typeof v !== "object" || (v as { version?: unknown }).version !== 1)
      return validation("spec.version must be 1");
    const spec = v as TemplateSpec;
    const problems = checkSpec(spec);
    return problems.length ? templateInvalid(problems) : spec;
  }

  const paramsOf = (body: unknown): Record<string, string> | Response => {
    const raw = (body as { params?: unknown } | null)?.params;
    if (raw === undefined) return {};
    if (!raw || typeof raw !== "object" || Array.isArray(raw))
      return validation("params must be an object");
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(raw)) {
      if (typeof v !== "string") return validation(`params.${k} must be a string`);
      out[k] = v;
    }
    return out;
  };

  const templateOf = (p: Principal, id: string | undefined) =>
    templatesOf(p.company).find((t) => t.id === id) ?? null;

  function newRecurring(
    p: Principal,
    responsibilityId: string,
    schedule: string,
    task: TaskTemplate,
    at: string,
  ): RecurringWork {
    const row: RecurringWork = {
      company_id: p.company.id,
      id: uid(0x7b00, d.nextSeq()),
      responsibility_id: responsibilityId,
      schedule,
      board_id: null,
      task_template: task,
      status: "active",
      next_run_at: null,
      last_materialised_at: null,
      ...actor(p),
      created_at: at,
      updated_at: at,
    };
    row.next_run_at = nextOf(p, row, d.now());
    recurringOf(p.company).push(row);
    return row;
  }

  // CHART-KB: goals --------------------------------------------------------------------------

  /** 05 `goal` editor: a manager, or a holder of the seat. */
  function canWriteGoals(p: Principal, seatId: string): boolean {
    if (p.manager) return true;
    const mine = new Set(
      p.me.memberships.filter((m) => m.company_id === p.company.id).map((m) => m.membership_id),
    );
    return d
      .assignmentsOf(p.company)
      .some((a) => a.active_to === null && a.seat_id === seatId && mine.has(a.membership_id));
  }
  /**
   * PRIORITIES-HOME: who sees a seat's priorities (the Keystones audience, the chart's rule:
   * a manager always, else `everyone`, the reporting line or the holder) and which seats the
   * caller may write. `goals.list` reads it too: a seat outside the audience is a 403 unless the
   * caller holds it.
   */
  function goalStanding(p: Principal) {
    const mine = new Set(
      p.me.memberships.filter((m) => m.company_id === p.company.id).map((m) => m.membership_id),
    );
    const seats = d.seatsOf(p.company).filter((s) => s.status !== "proposed");
    const held = new Set(
      d
        .assignmentsOf(p.company)
        .filter((a) => a.active_to === null && mine.has(a.membership_id))
        .map((a) => a.seat_id),
    );
    const sup = new Map(seats.map((s) => [s.id, s.supervisor_seat_id]));
    const above = (id: string) => {
      let cur = sup.get(id) ?? null;
      const seen = new Set<string>();
      while (cur && !seen.has(cur)) {
        if (held.has(cur)) return true;
        seen.add(cur);
        cur = sup.get(cur) ?? null;
      }
      return false;
    };
    const level = p.company.settings.chart?.visibility?.keystones ?? "everyone";
    // KPI-VISIBILITY: a grant naming the caller for priorities adds its seats to the level.
    const standing = d.kpiStanding?.(p);
    const sees = (id: string) =>
      p.manager ||
      level === "everyone" ||
      (level === "reporting_line" && (held.has(id) || above(id))) ||
      (level === "holder" && held.has(id)) ||
      (standing?.grants_reach("priorities", id) ?? false);
    const writable = new Set(
      seats.filter((s) => s.status !== "closed" && (p.manager || held.has(s.id))).map((s) => s.id),
    );
    // The org chart's viewer rule: a manager, a member, or someone holding a seat.
    const viewer = p.manager || p.roles.includes("member") || held.size > 0;
    return { sees, held, viewer, writable };
  }
  type MilestoneInput = { id?: string; title?: unknown; due_at?: unknown; done?: unknown };
  /** The API's `writeMilestones`: rows with an id are kept and updated, new ones added, the rest removed. */
  function writeMilestones(
    p: Principal,
    goal: Goal,
    items: MilestoneInput[],
  ): GoalMilestone[] | Response {
    const at = stamp();
    const byId = new Map(goal.milestones.map((m) => [m.id, m]));
    const out: GoalMilestone[] = [];
    for (const [position, item] of items.entries()) {
      if (typeof item.title !== "string" || !item.title.trim())
        return validation(`milestones[${position}].title must not be empty`);
      const existing = item.id !== undefined ? byId.get(item.id) : undefined;
      if (item.id !== undefined && !existing)
        return validation(`milestones[${position}].id: no such milestone on this goal`);
      const doneAt =
        item.done === undefined
          ? (existing?.done_at ?? null)
          : item.done
            ? (existing?.done_at ?? at)
            : null;
      out.push({
        company_id: p.company.id,
        id: existing?.id ?? uid(0x2f80 + d.nextSeq(), position + 1),
        goal_id: goal.id,
        title: item.title.trim(),
        due_at:
          item.due_at === undefined
            ? (existing?.due_at ?? null)
            : typeof item.due_at === "string" && item.due_at
              ? new Date(item.due_at).toISOString()
              : null,
        done_at: doneAt,
        position,
        ...actor(p),
        created_at: existing?.created_at ?? at,
        updated_at: at,
      });
    }
    return out;
  }
  const QUARTER = /^\d{4}-Q[1-4]$/;
  const goalDetail = (g: Goal): Goal => ({
    ...g,
    milestones: [...g.milestones].sort((a, b) => a.position - b.position),
  });

  // Handlers -----------------------------------------------------------------------------------

  return {
    "goals.list": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      const seat = seatOf(p, params.id);
      if (!seat) return notFound();
      // PRIORITIES-HOME's rule (the Keystones audience), plus the seat's writers.
      const { sees, held, viewer } = goalStanding(p);
      if (!viewer || (!sees(seat.id) && !held.has(seat.id))) return forbidden();
      const kind = url.searchParams.get("kind");
      const period = url.searchParams.get("period_key");
      const state = url.searchParams.get("state");
      const rows = goalsOf(p.company)
        .filter((g) => g.seat_id === seat.id)
        .filter((g) => !kind || g.kind === kind)
        .filter((g) => !period || g.period_key === period)
        .filter((g) => !state || g.state === state)
        // Priorities first, the latest quarter first, then title.
        .sort(
          (a, b) =>
            Number(b.kind === "priority") - Number(a.kind === "priority") ||
            (b.period_key ?? "").localeCompare(a.period_key ?? "") ||
            a.title.localeCompare(b.title),
        )
        .map(goalDetail);
      return json(d.page(rows, url));
    },
    "goals.create": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const seat = seatOf(p, params.id);
      if (!seat) return notFound();
      if (!canWriteGoals(p, seat.id)) return forbidden();
      if (seat.status === "closed") return problem(409, "seat_closed", "The seat is closed");
      const b = (body ?? {}) as {
        kind?: unknown;
        title?: unknown;
        period_key?: unknown;
        due_at?: unknown;
        track?: unknown;
        milestones?: unknown;
      };
      if (typeof b.title !== "string" || !b.title.trim())
        return validation("title must not be empty");
      const kind = (b.kind ?? "priority") as Goal["kind"];
      if (!["goal", "priority", "annual"].includes(kind)) return validation("kind");
      const period =
        b.period_key === undefined
          ? kind === "priority"
            ? quarterOf(d.now())
            : null
          : typeof b.period_key === "string"
            ? b.period_key
            : null;
      if (kind === "priority" && (!period || !QUARTER.test(period)))
        return validation("period_key: a priority names a quarter (2026-Q4)");
      if (b.track !== undefined && !["on", "off", "at_risk"].includes(String(b.track)))
        return validation("track: on, off or at_risk");
      const at = stamp();
      const goal: Goal = {
        company_id: p.company.id,
        id: uid(0x2f00 + d.nextSeq(), 1),
        kind,
        seat_id: seat.id,
        team_id: null,
        title: b.title.trim(),
        due_at: typeof b.due_at === "string" && b.due_at ? new Date(b.due_at).toISOString() : null,
        period_key: period,
        metric_def_id: null,
        target: null,
        state: "open",
        track: (b.track as Goal["track"] | undefined) ?? "on",
        flagged_to_portfolio: false,
        portfolio_link_ref: null,
        vision_page_id: null,
        ...actor(p),
        created_at: at,
        updated_at: at,
        milestones: [],
      };
      if (b.milestones !== undefined) {
        if (!Array.isArray(b.milestones)) return validation("milestones must be a list");
        const rows = writeMilestones(p, goal, b.milestones as MilestoneInput[]);
        if (rows instanceof Response) return rows;
        goal.milestones = rows;
      }
      goalsOf(p.company).push(goal);
      return json(goalDetail(goal), 201);
    },
    "goals.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      const goal = goalsOf(p.company).find((g) => g.id === params.id);
      if (!goal) return notFound();
      if (!canWriteGoals(p, goal.seat_id)) return forbidden();
      const b = (body ?? {}) as {
        title?: unknown;
        period_key?: unknown;
        due_at?: unknown;
        state?: unknown;
        track?: unknown;
        milestones?: unknown;
      };
      if (b.title !== undefined && (typeof b.title !== "string" || !b.title.trim()))
        return validation("title must not be empty");
      if (b.track !== undefined && !["on", "off", "at_risk"].includes(String(b.track)))
        return validation("track: on, off or at_risk");
      if (
        b.state !== undefined &&
        !["open", "achieved", "missed", "dropped"].includes(String(b.state))
      )
        return validation("state");
      if (
        goal.kind === "priority" &&
        b.period_key !== undefined &&
        (typeof b.period_key !== "string" || !QUARTER.test(b.period_key))
      )
        return validation("period_key: a priority names a quarter (2026-Q4)");
      let milestones = goal.milestones;
      if (b.milestones !== undefined) {
        if (!Array.isArray(b.milestones)) return validation("milestones must be a list");
        const rows = writeMilestones(p, goal, b.milestones as MilestoneInput[]);
        if (rows instanceof Response) return rows;
        milestones = rows;
      }
      Object.assign(goal, {
        ...(typeof b.title === "string" ? { title: b.title.trim() } : {}),
        ...(typeof b.period_key === "string" ? { period_key: b.period_key } : {}),
        ...(b.due_at !== undefined
          ? {
              due_at:
                typeof b.due_at === "string" && b.due_at ? new Date(b.due_at).toISOString() : null,
            }
          : {}),
        ...(b.state !== undefined ? { state: b.state as Goal["state"] } : {}),
        ...(b.track !== undefined ? { track: b.track as Goal["track"] } : {}),
        milestones,
        ...actor(p),
        updated_at: stamp(),
      });
      return json(goalDetail(goal));
    },
    "goals.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      const list = goalsOf(p.company);
      const i = list.findIndex((g) => g.id === params.id);
      const goal = list[i];
      if (!goal) return notFound();
      if (!canWriteGoals(p, goal.seat_id)) return forbidden();
      list.splice(i, 1);
      return noContent();
    },
    // PRIORITIES-HOME: the quarter's board cut to the Keystones audience, and the roll-over, as
    // services/api/src/routes/org/priorities.ts.
    "priorities.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      const current = quarterOf(d.now());
      const quarter = url.searchParams.get("quarter") ?? current;
      if (!QUARTER.test(quarter)) return validation("quarter: a quarter key (2026-Q4)");
      const { sees, writable } = goalStanding(p);
      const rows = goalsOf(p.company).filter(
        (g) => g.kind === "priority" && g.period_key === quarter,
      );
      const next = nextQuarterOf(current);
      const keys = goalsOf(p.company).flatMap((g) =>
        g.kind === "priority" && g.period_key ? [g.period_key] : [],
      );
      return json({
        quarter,
        current_quarter: current,
        next_quarter: next,
        quarters: [...new Set([...keys, current, next, quarter])].sort().reverse(),
        visibility: p.company.settings.chart?.visibility?.keystones ?? "everyone",
        hidden_seats: new Set(rows.filter((g) => !sees(g.seat_id)).map((g) => g.seat_id)).size,
        writable_seat_ids: [...writable],
        data: rows.filter((g) => sees(g.seat_id)).map(goalDetail),
      });
    },
    "priorities.carryOver": ({ principal: p, body }) => {
      if (!p) return notFound();
      const b = (body ?? {}) as { from?: unknown; to?: unknown; seat_ids?: unknown };
      if (typeof b.from !== "string" || !QUARTER.test(b.from)) return validation("from");
      if (typeof b.to !== "string" || !QUARTER.test(b.to)) return validation("to");
      if (b.from === b.to) return validation("to: carry over into another quarter");
      const { writable } = goalStanding(p);
      const named = Array.isArray(b.seat_ids) ? (b.seat_ids as string[]) : null;
      for (const id of named ?? []) if (!seatOf(p, id)) return notFound();
      for (const id of named ?? []) if (!writable.has(id)) return forbidden();
      if (!named && writable.size === 0) return forbidden();
      const seats = new Set(named ?? writable);
      const list = goalsOf(p.company);
      const taken = new Set(
        list.filter((g) => g.period_key === b.to).map((g) => `${g.seat_id}\u0000${g.title}`),
      );
      const created: Goal[] = [];
      let skipped = 0;
      for (const g of list.filter(
        (x) =>
          x.kind === "priority" &&
          x.period_key === b.from &&
          x.state === "open" &&
          seats.has(x.seat_id),
      )) {
        if (taken.has(`${g.seat_id}\u0000${g.title}`)) {
          skipped += 1;
          continue;
        }
        const at = stamp();
        const id = uid(0x2f00 + d.nextSeq(), 1);
        const copy: Goal = {
          ...g,
          id,
          period_key: b.to,
          due_at: null,
          state: "open",
          ...actor(p),
          created_at: at,
          updated_at: at,
          milestones: g.milestones
            .filter((m) => m.done_at === null)
            .map((m, position) => ({
              ...m,
              id: uid(0x2f80 + d.nextSeq(), position + 1),
              goal_id: id,
              position,
              ...actor(p),
              created_at: at,
              updated_at: at,
            })),
        };
        Object.assign(g, { state: "missed", ...actor(p), updated_at: at });
        created.push(copy);
      }
      list.push(...created);
      return json({ created: created.map(goalDetail), closed: created.length, skipped });
    },

    "responsibilities.list": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      const seat = seatOf(p, params.id);
      if (!seat) return notFound();
      if (!canViewSeat(p, seat)) return notFound(); // the API hides a seat the caller cannot view (404)
      return json(
        d.page(
          responsibilitiesOf(p.company).filter((r) => r.seat_id === params.id),
          url,
        ),
      );
    },
    "responsibilities.create": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const seat = seatOf(p, params.id);
      if (!seat) return notFound();
      if (seat.status === "closed") return problem(409, "seat_closed", "Seat is closed");
      const b = (body ?? {}) as {
        name?: unknown;
        description?: unknown;
        weight?: unknown;
        kpi_metric_ids?: unknown;
      };
      if (typeof b.name !== "string" || !b.name.trim()) return validation("name must not be empty");
      const weight = b.weight === undefined ? "1.000" : weightOf(b.weight);
      if (weight === null)
        return validation("weight: must be ≥ 0 and < 1000, at most 3 fraction digits");
      const ids = Array.isArray(b.kpi_metric_ids) ? (b.kpi_metric_ids as string[]) : [];
      const known = new Set(d.metricsOf(p.company).map((m) => m.id));
      if (ids.some((id) => !known.has(id))) return validation("kpi_metric_ids: no such metric");
      const at = stamp();
      const row: Responsibility = {
        company_id: p.company.id,
        id: uid(0x7a00, d.nextSeq()),
        seat_id: seat.id,
        name: b.name.trim(),
        description:
          typeof b.description === "string" && b.description.trim() ? b.description.trim() : null,
        weight,
        kpi_metric_ids: [...new Set(ids)],
        status: "active",
        ...actor(p),
        created_at: at,
        updated_at: at,
      };
      responsibilitiesOf(p.company).push(row);
      return json(row, 201);
    },
    "responsibilities.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const row = responsibilityOf(p, params.id);
      if (!row) return notFound();
      const b = (body ?? {}) as Partial<
        Record<"name" | "description" | "weight" | "kpi_metric_ids" | "status", unknown>
      >;
      if (b.name !== undefined && (typeof b.name !== "string" || !b.name.trim()))
        return validation("name must not be empty");
      const weight = b.weight === undefined ? row.weight : weightOf(b.weight);
      if (weight === null)
        return validation("weight: must be ≥ 0 and < 1000, at most 3 fraction digits");
      if (b.status !== undefined && b.status !== "active" && b.status !== "archived")
        return validation("status: active or archived");
      if (b.kpi_metric_ids !== undefined) {
        const known = new Set(d.metricsOf(p.company).map((m) => m.id));
        if (!Array.isArray(b.kpi_metric_ids) || b.kpi_metric_ids.some((id) => !known.has(id)))
          return validation("kpi_metric_ids: no such metric");
      }
      const at = stamp();
      Object.assign(row, {
        ...(typeof b.name === "string" ? { name: b.name.trim() } : {}),
        ...(b.description !== undefined
          ? {
              description:
                typeof b.description === "string" && b.description.trim()
                  ? b.description.trim()
                  : null,
            }
          : {}),
        weight,
        ...(b.kpi_metric_ids !== undefined
          ? { kpi_metric_ids: [...new Set(b.kpi_metric_ids as string[])] }
          : {}),
        ...(b.status !== undefined ? { status: b.status } : {}),
        updated_at: at,
      });
      // Archiving pauses its active recurring work; restoring resumes nothing.
      if (b.status === "archived")
        for (const w of recurringOf(p.company))
          if (w.responsibility_id === row.id && w.status === "active")
            Object.assign(w, { status: "paused", next_run_at: null, updated_at: at });
      return json(row);
    },
    "responsibilities.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const list = responsibilitiesOf(p.company);
      const i = list.findIndex((r) => r.id === params.id);
      if (i < 0) return notFound();
      list.splice(i, 1);
      const work = recurringOf(p.company);
      for (let j = work.length - 1; j >= 0; j--)
        if (work[j]?.responsibility_id === params.id) work.splice(j, 1);
      return noContent();
    },

    "recurringWork.list": ({ principal: p, params, url }) => {
      if (!p) return notFound();
      const parent = responsibilityOf(p, params.id);
      const seat = parent ? seatOf(p, parent.seat_id) : null;
      if (!parent || !seat) return notFound();
      if (!canViewSeat(p, seat)) return notFound(); // the API hides a seat the caller cannot view (404)
      return json(
        d.page(
          recurringOf(p.company).filter((w) => w.responsibility_id === params.id),
          url,
        ),
      );
    },
    "recurringWork.create": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const parent = responsibilityOf(p, params.id);
      if (!parent) return notFound();
      if (seatOf(p, parent.seat_id)?.status === "closed")
        return problem(409, "seat_closed", "Seat is closed");
      if (parent.status === "archived")
        return problem(409, "conflict", "Conflict", "The responsibility is archived.");
      const b = (body ?? {}) as { schedule?: unknown; board_id?: unknown; task_template?: unknown };
      const schedule = scheduleText(b.schedule);
      if (schedule instanceof Response) return schedule;
      // Boards do not exist in the fake (M2-03): any board id names nothing.
      if (b.board_id) return validation("board_id: no such board");
      const task = taskTemplate(b.task_template);
      if (typeof task === "string") return validation(task);
      return json(newRecurring(p, parent.id, schedule, task, stamp()), 201);
    },
    "recurringWork.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const row = recurringRow(p, params.id);
      if (!row) return notFound();
      const b = (body ?? {}) as {
        schedule?: unknown;
        board_id?: unknown;
        task_template?: unknown;
        status?: unknown;
      };
      let schedule = row.schedule;
      if (b.schedule !== undefined) {
        const text = scheduleText(b.schedule);
        if (text instanceof Response) return text;
        schedule = text;
      }
      if (b.board_id) return validation("board_id: no such board");
      const task =
        b.task_template === undefined ? row.task_template : taskTemplate(b.task_template);
      if (typeof task === "string") return validation(task);
      if (b.status !== undefined && b.status !== "active" && b.status !== "paused")
        return validation("status: active or paused");
      if (b.status === "active" && row.status === "paused") {
        const parent = responsibilityOf(p, row.responsibility_id);
        if (parent?.status === "archived")
          return problem(409, "conflict", "Conflict", "Its responsibility is archived.");
        if (seatOf(p, parent?.seat_id)?.status === "closed")
          return problem(409, "conflict", "Conflict", "Its seat is closed.");
      }
      Object.assign(row, {
        schedule,
        task_template: task,
        ...(b.status !== undefined ? { status: b.status } : {}),
        updated_at: stamp(),
      });
      row.next_run_at = row.status === "active" ? nextOf(p, row, d.now()) : null;
      return json(row);
    },
    "recurringWork.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const list = recurringOf(p.company);
      const i = list.findIndex((w) => w.id === params.id);
      if (i < 0) return notFound();
      list.splice(i, 1);
      return noContent();
    },
    "recurringWork.materialiseNow": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const row = recurringRow(p, params.id);
      if (!row) return notFound();
      if (row.status !== "active" || !row.next_run_at)
        return problem(409, "conflict", "Conflict", "The schedule is paused or has ended.");
      // The occurrence at next_run_at is recorded now (no task until boards exist); the next
      // run is the first occurrence after max(that occurrence, now).
      const due = new Date(row.next_run_at);
      const now = d.now();
      Object.assign(row, {
        last_materialised_at: now.toISOString(),
        next_run_at: nextOf(p, row, due > now ? due : now),
        updated_at: now.toISOString(),
      });
      return json(row);
    },

    "templates.list": ({ principal: p, url }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      return json(d.page(templatesOf(p.company), url));
    },
    "templates.get": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const t = templateOf(p, params.id);
      return t ? json(t) : notFound();
    },
    "templates.create": ({ principal: p, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const b = (body ?? {}) as { name?: unknown; description?: unknown; spec?: unknown };
      if (typeof b.name !== "string" || !b.name.trim()) return validation("name must not be empty");
      const name = b.name.trim();
      if (templatesOf(p.company).some((t) => t.name.toLowerCase() === name.toLowerCase()))
        return problem(409, "conflict", "Conflict", "A template with this name exists.");
      const spec = specOf(b.spec);
      if (spec instanceof Response) return spec;
      const at = stamp();
      const t: Template = {
        company_id: p.company.id,
        id: uid(0x7e10, d.nextSeq()),
        name,
        description: typeof b.description === "string" ? b.description : null,
        spec,
        version: 1,
        ...actor(p),
        created_at: at,
        updated_at: at,
      };
      templatesOf(p.company).push(t);
      return json(t, 201);
    },
    "templates.update": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const t = templateOf(p, params.id);
      if (!t) return notFound();
      const b = (body ?? {}) as { name?: unknown; description?: unknown; spec?: unknown };
      if (b.name !== undefined) {
        if (typeof b.name !== "string" || !b.name.trim())
          return validation("name must not be empty");
        const name = b.name.trim().toLowerCase();
        if (templatesOf(p.company).some((x) => x.id !== t.id && x.name.toLowerCase() === name))
          return problem(409, "conflict", "Conflict", "A template with this name exists.");
      }
      let spec = t.spec;
      if (b.spec !== undefined) {
        const parsed = specOf(b.spec);
        if (parsed instanceof Response) return parsed;
        spec = parsed;
      }
      const changed = JSON.stringify(spec) !== JSON.stringify(t.spec);
      Object.assign(t, {
        ...(typeof b.name === "string" ? { name: b.name.trim() } : {}),
        ...(b.description !== undefined
          ? { description: typeof b.description === "string" ? b.description : null }
          : {}),
        spec,
        version: changed ? t.version + 1 : t.version,
        updated_at: stamp(),
      });
      return json(t);
    },
    "templates.delete": ({ principal: p, params }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const list = templatesOf(p.company);
      const i = list.findIndex((t) => t.id === params.id);
      if (i < 0) return notFound();
      list.splice(i, 1);
      return noContent();
    },
    "templates.preview": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const t = templateOf(p, params.id);
      if (!t) return notFound();
      const given = paramsOf(body);
      if (given instanceof Response) return given;
      const result = plan(p, t, given);
      const preview: TemplatePreview = {
        template_id: t.id,
        version: t.version,
        valid: result.problems.length === 0,
        problems: result.problems,
        params: result.params,
        items: result.items,
      };
      return json(preview);
    },
    "templates.apply": ({ principal: p, params, body }) => {
      if (!p) return notFound();
      if (!p.manager) return forbidden();
      const t = templateOf(p, params.id);
      if (!t) return notFound();
      const given = paramsOf(body);
      if (given instanceof Response) return given;
      const result = plan(p, t, given);
      if (result.problems.length) return templateInvalid(result.problems);
      const { spec } = result;
      const at = stamp();
      const who = actor(p);
      const created: TemplateApplied["created"] = {
        roles: [],
        teams: [],
        seats: [],
        responsibilities: [],
        recurring_work: [],
        spaces: [],
        channels: [],
        boards: [],
        tasks: [],
      };
      const roleIds = new Map(result.reuse.roles);
      for (const r of spec.roles ?? []) {
        if (roleIds.has(r.key)) continue;
        const role: Role = {
          company_id: p.company.id,
          id: uid(0x2f10, d.nextSeq()),
          name: r.name,
          description: r.description ?? null,
          default_metric_keys: [],
          ...who,
          created_at: at,
          updated_at: at,
        };
        d.rolesOf(p.company.id).push(role);
        roleIds.set(r.key, role.id);
        created.roles.push(role.id);
      }
      const seatIds = new Map<string, string>();
      const seatList = d.seatsOf(p.company);
      for (const s of spec.seats ?? []) {
        const seat: Seat = {
          company_id: p.company.id,
          id: uid(0x2b00, d.nextSeq()),
          role_id: roleIds.get(s.role) ?? "",
          title: s.title,
          supervisor_seat_id: null,
          staff: false,
          kind: "standard",
          status: "open",
          origin: "template",
          origin_ref: { system: "template", id: t.id },
          settings: {},
          de_fill: null,
          version: 1,
          ...who,
          created_at: at,
          updated_at: at,
        };
        seatList.push(seat);
        seatIds.set(s.key, seat.id);
        created.seats.push(seat.id);
      }
      for (const s of spec.seats ?? []) {
        const seat = seatList.find((x) => x.id === seatIds.get(s.key));
        if (seat && s.supervisor) seat.supervisor_seat_id = seatIds.get(s.supervisor) ?? null;
      }
      const teamIds = new Map(result.reuse.teams);
      for (const team of spec.teams ?? []) {
        if (teamIds.has(team.key)) continue;
        const row: FakeTeam = {
          company_id: p.company.id,
          id: uid(0x7c00, d.nextSeq()),
          name: team.name,
        };
        teamsOf(p.company).push(row);
        teamIds.set(team.key, row.id);
        created.teams.push(row.id);
      }
      // Spaces, channels, boards, starter tasks: through the work twin (before the recurring
      // work, which names its board by key).
      const boardIds = new Map<string, string>();
      if (result.handlers)
        for (const s of spec.spaces ?? []) {
          const { boards, ...space } = s;
          const teamId = s.team ? (teamIds.get(s.team) ?? null) : null;
          const made = result.handlers.space(p, { templateId: t.id, space, teamId });
          created.spaces.push(made.spaceId);
          created.channels.push(...made.channelIds);
          for (const b of boards ?? []) {
            const madeBoard = result.handlers.board(p, {
              templateId: t.id,
              spaceId: made.spaceId,
              board: b,
            });
            if (madeBoard instanceof Response) return madeBoard;
            created.boards.push(madeBoard.boardId);
            boardIds.set(b.key, madeBoard.boardId);
            created.channels.push(...madeBoard.channelIds);
            created.tasks.push(...madeBoard.taskIds);
          }
        }
      for (const r of spec.responsibilities ?? []) {
        const row: Responsibility = {
          company_id: p.company.id,
          id: uid(0x7a00, d.nextSeq()),
          seat_id: seatIds.get(r.seat) ?? "",
          name: r.name,
          description: r.description ?? null,
          weight: "1.000",
          kpi_metric_ids: (r.metric_keys ?? []).flatMap((k) => {
            const id = result.metrics.get(k);
            return id ? [id] : [];
          }),
          status: "active",
          ...who,
          created_at: at,
          updated_at: at,
        };
        responsibilitiesOf(p.company).push(row);
        created.responsibilities.push(row.id);
        for (const x of r.recurring ?? []) {
          const rule = ruleOf(x.schedule);
          const w = newRecurring(p, row.id, rule ? formatRule(rule) : x.schedule, x.task, at);
          if (x.board) w.board_id = boardIds.get(x.board) ?? null;
          created.recurring_work.push(w.id);
        }
      }
      const applied: TemplateApplied = {
        template_id: t.id,
        version: t.version,
        created,
        skipped: result.items.filter((i) => i.action === "skip"),
      };
      return json(applied, 201);
    },
  };
}
